import React, { useState, useEffect } from 'react';
import { MdClose, MdDescription, MdCode } from 'react-icons/md';

const FileModal = ({ isOpen, onClose, onCreateFile, projects = [], selectedProject, loading }) => {
  const [formData, setFormData] = useState({
    fileName: '',
    fileType: 'js',
    projectId: null
  });

  // Update project selection when selectedProject prop changes
  useEffect(() => {
    if (selectedProject) {
      setFormData(prev => ({ ...prev, projectId: selectedProject.project_id }));
    } else {
      setFormData(prev => ({ ...prev, projectId: null }));
    }
  }, [selectedProject]);

  const fileTypes = [
    { value: 'js', label: 'JavaScript (.js)', ext: '.js' },
    { value: 'py', label: 'Python (.py)', ext: '.py' },
    { value: 'java', label: 'Java (.java)', ext: '.java' },
    { value: 'cpp', label: 'C++ (.cpp)', ext: '.cpp' },
    { value: 'matlab', label: 'MATLAB (.m)', ext: '.m' },
    { value: 'html', label: 'HTML (.html)', ext: '.html' },
    { value: 'css', label: 'CSS (.css)', ext: '.css' },
    { value: 'sql', label: 'SQL (.sql)', ext: '.sql' },
    { value: 'json', label: 'JSON (.json)', ext: '.json' },
    { value: 'md', label: 'Markdown (.md)', ext: '.md' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fileName.trim()) {
      return;
    }

    const selectedType = fileTypes.find(type => type.value === formData.fileType);
    const fullFileName = formData.fileName.includes('.')
      ? formData.fileName.trim()
      : formData.fileName.trim() + selectedType.ext;

    const success = await onCreateFile(fullFileName, formData.projectId);

    if (success) {
      // Reset form and close modal
      setFormData({
        fileName: '',
        fileType: 'js',
        projectId: null
      });
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <MdDescription className="text-green-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Create New File</h2>
              <p className="text-sm text-gray-600">
                Choose location and configure your new file
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <MdClose size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <select
              value={formData.projectId || ''}
              onChange={(e) => handleInputChange('projectId', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="">
                📄 Standalone File
              </option>
              {projects.map(project => (
                <option key={project.project_id} value={project.project_id}>
                  📁 {project.name} ({project.runtime_language})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Choose where to create the file
            </p>
          </div>

          {/* File Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.fileName}
              onChange={(e) => handleInputChange('fileName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter file name..."
              required
              disabled={loading}
            />
          </div>

          {/* File Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.fileType}
              onChange={(e) => handleInputChange('fileType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={loading}
            >
              {fileTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Extension will be added automatically if not provided
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
              disabled={loading || !formData.fileName.trim()}
            >
              <MdCode size={16} />
              <span>{loading ? 'Creating...' : 'Create File'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FileModal;